// §3.1 ceilings, §14 normalization, and §14 root inference.
import type { FileInput } from "./types.ts";

// §3.1 ceilings. "MB" here is binary (1024*1024); these bounds are tunable (§3.1).
export const MAX_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_TOTAL_BYTES = 25 * 1024 * 1024;
export const MAX_FILE_COUNT = 512;

/** A normalized file: absolute, `/`-rooted, `/`-separated path plus its bytes. */
export interface NormalFile {
  path: string; // always begins with "/"
  bytes: Uint8Array;
}

export interface NormalizedTree {
  files: NormalFile[];
  /** The inferred entry document path (already in `files`), or null if one must be synthesized. */
  entryPath: string | null;
}

export class BuildError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BuildError";
  }
}

// OS junk to strip (§14). Matched on the basename; `__MACOSX` matched as a path segment.
const JUNK_BASENAMES = new Set([".DS_Store", "Thumbs.db", "desktop.ini"]);

function isJunk(segments: string[]): boolean {
  const base = segments[segments.length - 1]!;
  if (JUNK_BASENAMES.has(base)) return true;
  if (base.startsWith("._")) return true; // AppleDouble resource forks
  if (segments.includes("__MACOSX")) return true; // macOS zip metadata dir
  return false;
}

/**
 * Validate and normalize a single raw path to `/`-rooted `/`-separated form.
 * Returns null for OS junk (to be dropped). Throws BuildError on unsafe paths (§14).
 */
function normalizeOne(raw: string): string | null {
  if (raw.includes("\0")) throw new BuildError(`path contains a null byte: ${JSON.stringify(raw)}`);
  if (raw.includes("\\")) throw new BuildError(`path contains a backslash (rejected): ${JSON.stringify(raw)}`);
  if (raw.startsWith("/")) throw new BuildError(`path has a leading slash (rejected): ${JSON.stringify(raw)}`);

  const segments: string[] = [];
  for (const seg of raw.split("/")) {
    if (seg === "" || seg === ".") continue; // collapse `//`, drop `.`
    if (seg === "..") throw new BuildError(`path escapes root via '..': ${JSON.stringify(raw)}`);
    segments.push(seg);
  }
  if (segments.length === 0) throw new BuildError(`empty path after normalization: ${JSON.stringify(raw)}`);
  if (isJunk(segments)) return null;
  return "/" + segments.join("/");
}

/** Strip a single shared top-level directory prefix if every file lives under it (§14). */
function stripSharedPrefix(files: NormalFile[]): NormalFile[] {
  if (files.length === 0) return files;
  let shared: string | null = null;
  for (const f of files) {
    const parts = f.path.slice(1).split("/");
    if (parts.length < 2) return files; // a top-level file exists → no shared dir prefix
    const first = parts[0]!;
    if (shared === null) shared = first;
    else if (shared !== first) return files;
  }
  if (shared === null) return files;
  return files.map((f) => ({ path: "/" + f.path.slice(1).split("/").slice(1).join("/"), bytes: f.bytes }));
}

/**
 * §3.1 ceilings + §14 normalize + §14 root inference.
 * Ceilings are enforced against the raw author input, before synthesis (§6 step 1).
 */
export function normalize(files: FileInput[], entryHint?: string): NormalizedTree {
  // Ceilings (§3.1) — count and per-file/total bytes on the raw input.
  if (files.length > MAX_FILE_COUNT) {
    throw new BuildError(`file count ${files.length} exceeds ceiling of ${MAX_FILE_COUNT} (§3.1)`);
  }
  let total = 0;
  for (const f of files) {
    if (f.bytes.byteLength > MAX_FILE_BYTES) {
      throw new BuildError(
        `file ${JSON.stringify(f.path)} is ${f.bytes.byteLength} bytes, exceeds per-file ceiling ${MAX_FILE_BYTES} (§3.1)`,
      );
    }
    total += f.bytes.byteLength;
  }
  if (total > MAX_TOTAL_BYTES) {
    throw new BuildError(`total ${total} bytes exceeds ceiling ${MAX_TOTAL_BYTES} (§3.1)`);
  }

  // Normalize + reject symlinks + strip junk.
  const seen = new Map<string, Uint8Array>();
  for (const f of files) {
    if (f.symlink) throw new BuildError(`symlink entry rejected: ${JSON.stringify(f.path)} (§14)`);
    const path = normalizeOne(f.path);
    if (path === null) continue; // dropped junk
    if (seen.has(path)) throw new BuildError(`duplicate path after normalization: ${JSON.stringify(path)}`);
    seen.set(path, f.bytes);
  }

  let normal: NormalFile[] = [...seen].map(([path, bytes]) => ({ path, bytes }));
  normal = stripSharedPrefix(normal);
  // Deterministic ordering by path.
  normal.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));

  const paths = new Set(normal.map((f) => f.path));

  // Root inference (§14): guarantee a resolvable entry document.
  let entryPath: string | null = null;
  if (entryHint) {
    const hint = entryHint.startsWith("/") ? entryHint : "/" + entryHint;
    if (!paths.has(hint)) throw new BuildError(`entry ${JSON.stringify(entryHint)} not present in files`);
    entryPath = hint;
  } else if (paths.has("/index.html")) {
    entryPath = "/index.html";
  } else {
    const htmls = normal.filter((f) => f.path.endsWith(".html"));
    if (htmls.length === 1) entryPath = htmls[0]!.path;
    // else: entryPath stays null → synthesize an index (single-file link page or dir listing).
  }

  return { files: normal, entryPath };
}
